import cx from "classnames";
import { api, LightningElement } from "lwc";

import { createTrack, TrackEvent } from "common/track";
import { hash } from "common/helpers";

import type { NavItem, NavItemWithState } from "./types";
export type { NavItem };

const preloadedImages = new Set<string>();

export default class extends LightningElement {
  @api items: NavItem[] = [];
  @api layout: "desktop" | "mobile" = "desktop";

  private expandedNavItem?: NavItem = undefined;
  private track = createTrack<"tds-header-nav">(this);

  private get navItemsWithState(): NavItemWithState[] {
    const { expandedNavItem } = this;
    return this.items.map((item, index) => {
      const hasActiveItem = typeof expandedNavItem !== "undefined";
      const hasItems = typeof item.sections !== "undefined";
      // Can't compare navItems because of proxy
      const isExpanded =
        hasItems && hasActiveItem && item.label === expandedNavItem!.label;
      // HACK: For some reason, using the spread operator causes Chromatic break
      return Object.assign({}, item, {
        id: `_${hash(item.label)}`,
        className: cx("nav-list-item", {
          "nav-list-item--columns": item.sectionColumns,
          "nav-list-item--active": item.active,
        }),
        ariaCurrent: item.active ? "page" : null,
        index,
        isExpanded,
      });
    });
  }

  private get expandedIndex() {
    if (typeof this.expandedNavItem === "undefined") {
      return undefined;
    }
    return this.items.indexOf(this.expandedNavItem);
  }

  private handleNavItemTrack(e: TrackEvent<"tds-header-nav-item">) {
    this.track(e.detail.eventName, e.detail.payload);
  }

  private onItemClick(e: MouseEvent) {
    this.setActiveNavItem(e.currentTarget!);
  }

  private onItemFocus() {
    this.expandedNavItem = undefined;
  }

  private onLinkClick(e: MouseEvent) {
    this.track("item_clicked", {
      label: (e.currentTarget as HTMLElement).dataset.label ?? "",
    });
  }

  private onItemKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        this.setActiveNavItem(e.target!);
        break;
      }
      default:
        break;
    }
  }

  private onItemMouseEnter(e: FocusEvent) {
    this.setActiveNavItem(e.currentTarget!);
  }

  private onItemRequestClose() {
    let { expandedIndex } = this;
    this.expandedNavItem = undefined;
    // Refocus the nav item when the expanded menu requests to close itself
    // (usually the escape key)
    if (typeof expandedIndex !== "undefined") {
      let element = this.template.querySelectorAll(
        ".nav-list-item__link,.nav-list-item__button"
      )[expandedIndex] as HTMLElement;
      if (element) element.focus();
    }
  }

  private onFocusOut() {
    // The "focusout" event might have been triggered by another "focus" event.
    // After the next event loop, if the activeElement is null,
    // we know the nav is no longer focused
    // The 250ms timeout is for Safari:
    // Clicking a link in the menu causes the "focusout" event to be dispatched,
    // which closes the menu, but also prevents the navigation from happening
    setTimeout(() => {
      if (this.template.activeElement === null) {
        this.expandedNavItem = undefined;
      }
    }, 250);
  }

  private onMouseLeave() {
    this.expandedNavItem = undefined;
  }

  private preloadImages() {
    let urls = this.items.flatMap((i) =>
      (i.sections || []).flatMap((s) => s.items.flatMap((i) => i.iconUrl))
    );
    for (let url of urls) {
      if (!url) continue;
      if (preloadedImages.has(url)) continue;
      let image = new Image();
      image.src = url;
      preloadedImages.add(url);
    }
  }

  private setActiveNavItem(target: EventTarget) {
    let text = (target as HTMLElement).textContent;
    this.expandedNavItem = this.items.find((item) => item.label === text);
  }

  connectedCallback() {
    window.addEventListener("load", () => {
      this.preloadImages();
    });
  }
}
